import { useState } from 'react'
import type { Control } from 'react-hook-form'
import { router } from '@inertiajs/react'

import { Dialog } from '@atoms'
import { FormDialog } from '@components/molecules/FormDialog'
import Alert from '@components/atoms/Alert'
import Icon from '@components/utils/Icon'
import { Skeleton } from '@components/atoms/Skeleton'
import Checkbox from '@components/atoms/form/Checkbox'
import { currentLocale, scopedTranslation } from '@utils/I18n'
import useFormWithErrors from '@hooks/useFormWithErrors'

import usePublishRosterMutation from './usePublishRosterMutation'
import { usePublishPreviewQuery, type PublishPreview } from './usePublishPreviewQuery'
import { NotifiedVolunteers } from './NotifiedVolunteers'
import { PublishChangeList } from './PublishChangeList'

import { PublishNotificationKind } from '@frontend/graphql/types.generated'
import { useShiftsStore, publishShiftIdsArg, type PublishTarget } from '../roster_board/stores/useShiftsStore'
import type { EventWithRoster, ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('components.publish_roster')

type PublishForm = { acknowledged: boolean }

const scopeSummaryOf = (preview: PublishPreview) => {
  const parts = [
    preview.newShiftCount > 0 && t('scope_new', { count: preview.newShiftCount }),
    preview.editedShiftCount > 0 && t('scope_edited', { count: preview.editedShiftCount }),
    preview.removedShiftCount > 0 && t('scope_removed', { count: preview.removedShiftCount }),
  ].filter((part): part is string => Boolean(part))

  if (parts.length === 0) return null

  return new Intl.ListFormat(currentLocale(), { style: 'long', type: 'conjunction' }).format(parts)
}

const cancelledCountIn = (preview: PublishPreview) =>
  preview.recipients.filter((recipient) => recipient.kind === PublishNotificationKind.Unassigned).length

function PreviewSkeleton() {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3" role="status" aria-label={t('loading_preview')}>
      <Skeleton className="tw:h-4 tw:w-2/3" />
      <Skeleton className="tw:h-20 tw:w-full" />
    </div>
  )
}

type PublishSummaryProps = {
  preview: PublishPreview
  publishedShifts: ShiftWithSlots[]
  scopeSummary: string | null
  cancelledCount: number
  control: Control<PublishForm>
}

function PublishSummary({ preview, publishedShifts, scopeSummary, cancelledCount, control }: PublishSummaryProps) {
  const needsAcknowledgement = cancelledCount > 0

  return (
    <>
      {scopeSummary && (
        <Dialog.Description className="tw:text-sm tw:text-gray-600">
          {t('scope_prefix', { summary: scopeSummary })}
        </Dialog.Description>
      )}

      {preview.recipients.length === 0 && preview.claimableSpotCount === 0 && (
        <Alert type="info" size="sm">
          <span className="tw:text-sm">
            <b>{t('no_email_impact_title')}</b> {t('no_email_impact_detail')}
          </span>
        </Alert>
      )}

      <NotifiedVolunteers recipients={preview.recipients} />

      {preview.claimableSpotCount > 0 && (
        <div className="tw:flex tw:items-start tw:gap-2.5 tw:rounded-lg tw:border tw:border-gray-200 tw:px-3 tw:py-2.5">
          <Icon
            type="user-plus-01"
            className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400"
            aria-hidden
          />
          <p className="tw:text-sm tw:leading-normal tw:text-gray-600">
            {t('public_impact', { count: preview.claimableSpotCount })}
          </p>
        </div>
      )}

      <PublishChangeList shifts={publishedShifts} />

      {preview.understaffedShiftCount > 0 && (
        <Alert type="warning" size="sm" role="alert">
          <span className="tw:text-sm">
            {t('staff_gap_warning', {
              count: preview.understaffedShiftCount,
              spots: t('empty_spots', { count: preview.unfilledSpotCount }),
            })}
          </span>
        </Alert>
      )}

      {needsAcknowledgement ? (
        <Alert type="error" size="sm">
          <span className="tw:flex tw:items-start tw:gap-2 tw:[&>label]:max-w-none">
            <Checkbox name="acknowledged" control={control} tone="danger" className="tw:mt-0.5 tw:shrink-0">
              <span className="tw:text-sm tw:font-normal tw:text-gray-700">
                {t('acknowledge_cancellations', { count: cancelledCount })}
              </span>
            </Checkbox>
          </span>
        </Alert>
      ) : (
        preview.recipients.length > 0 && (
          <p className="tw:text-xs tw:text-gray-500">{t('irreversible_note')}</p>
        )
      )}
    </>
  )
}

type PublishRosterContentProps = {
  event: EventWithRoster
  publishTarget: PublishTarget
}

function PublishRosterContent({ event, publishTarget }: PublishRosterContentProps) {
  const closePublish = useShiftsStore((state) => state.closePublish)
  const clearSelection = useShiftsStore((state) => state.clearSelection)

  const [isPublishing, setIsPublishing] = useState(false)

  const { preview, isLoading, isError } = usePublishPreviewQuery({
    eventId: event.id,
    shiftIds: publishShiftIdsArg(publishTarget),
  })

  const { handleSubmit, control, watch } = useFormWithErrors({ defaultValues: { acknowledged: false } })
  const acknowledged = Boolean(watch('acknowledged'))
  const publishRoster = usePublishRosterMutation()

  const shiftCount = preview?.shiftCount ?? 0
  const cancelledCount = preview ? cancelledCountIn(preview) : 0
  const needsAcknowledgement = cancelledCount > 0
  const isPending = isLoading || (!preview && !isError)
  const isEmpty = Boolean(preview) && shiftCount === 0
  const isReady = Boolean(preview) && !isEmpty
  const scopeSummary = preview ? scopeSummaryOf(preview) : null

  const publishedShifts = (event.roster?.shifts?.nodes ?? []).filter((shift) => preview?.shiftIds.includes(shift.id))

  const handlePublish = async () => {
    if (!event.roster || !isReady) return
    if (needsAcknowledgement && !acknowledged) return

    setIsPublishing(true)

    await publishRoster({
      rosterId: event.roster.id,
      shiftIds: publishShiftIdsArg(publishTarget),
      onSuccess: () => {
        closePublish()
        clearSelection()
        router.reload({ only: ['event'] })
      },
    })

    setIsPublishing(false)
  }

  const title = () => {
    if (isPending) return t('publish_pending_title')
    if (isEmpty) return t('empty_title')

    return shiftCount === 1 ? t('publish_one_title') : t('publish_many_title', { count: shiftCount })
  }

  const submitText = () => {
    if (isPending) return t('publish')

    return shiftCount === 1 ? t('publish_one_confirm') : t('publish_many_confirm', { count: shiftCount })
  }

  return (
    <>
      <FormDialog.Title errorsPath="publishRoster">{title()}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handlePublish)}>
        <FormDialog.Body>
          {isPending && <PreviewSkeleton />}

          {isError && (
            <Alert type="error" size="sm" role="alert">
              <span className="tw:text-sm">{t('errors.unable_to_load_preview')}</span>
            </Alert>
          )}

          {isEmpty && (
            <>
              <Dialog.Description className="tw:text-sm tw:text-gray-600">{t('empty_lede')}</Dialog.Description>
              <Alert type="info" size="sm">
                <span className="tw:text-sm">{t('empty_detail')}</span>
              </Alert>
            </>
          )}

          {isReady && preview && (
            <PublishSummary
              preview={preview}
              publishedShifts={publishedShifts}
              scopeSummary={scopeSummary}
              cancelledCount={cancelledCount}
              control={control}
            />
          )}
        </FormDialog.Body>

        {isEmpty ? (
          <FormDialog.Footer showSubmit={false} />
        ) : (
          <FormDialog.Footer
            submitText={submitText()}
            submitColor={needsAcknowledgement ? 'danger' : 'primary'}
            isLoading={isPublishing || isPending}
            disabled={!isReady || (needsAcknowledgement && !acknowledged)}
          />
        )}
      </FormDialog.Form>
    </>
  )
}

export const PublishRosterDialog = ({ event }: { event: EventWithRoster }) => {
  const publishTarget = useShiftsStore((state) => state.publishTarget)
  const closePublish = useShiftsStore((state) => state.closePublish)

  const open = publishTarget !== null

  return (
    <FormDialog.Root open={open} onOpenChange={(next) => !next && closePublish()} size="lg">
      {open && <PublishRosterContent event={event} publishTarget={publishTarget} />}
    </FormDialog.Root>
  )
}
